import React, { useState } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../../shared/icons/Icons';
import { FormData, getCourtsByState, getCourtLocations } from '../../../shared/data/formData';
import { Button } from '../../../shared/components/ui/Button';
import { Select } from '../../../shared/components/ui/Select';
import { Input } from '../../../shared/components/ui/Input';
import { Card } from '../../../shared/components/ui/Card';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';
import { RadioCard } from '../../../shared/components/ui/RadioCard';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { SectionTitle } from '../../../shared/components/ui/SectionTitle';
import { InfoBanner } from '../../../shared/components/ui/InfoBanner';

interface CustomerServiceOfProcessProps {
  onNavigate: (page: string) => void;
}

const CustomerServiceOfProcess: React.FC<CustomerServiceOfProcessProps> = ({ onNavigate }) => {
  const brand = useBrand();
  const [step, setStep] = useState(1);
  const [serviceType, setServiceType] = useState('');

  const [jurisdiction, setJurisdiction] = useState('state');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCourt, setSelectedCourt] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [partyType, setPartyType] = useState('individual');
  const [addressState, setAddressState] = useState('');

  const courtOptions = jurisdiction === 'federal'
    ? FormData.federalCourts
    : (selectedState ? getCourtsByState(selectedState) : [{ value: '', label: 'Select state first...' }]);

  const locationOptions = selectedCourt
    ? getCourtLocations(selectedCourt)
    : [{ value: '', label: 'Select court first...' }];

  const handleJurisdictionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setJurisdiction(e.target.value);
    setSelectedCourt('');
    setSelectedLocation('');
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(e.target.value);
    setSelectedCourt('');
    setSelectedLocation('');
  };

  const handleCourtChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourt(e.target.value);
    setSelectedLocation('');
  };

  return (
    <div>
      <PageTitle subtitle="Choose the type of service to get started">Start a Process Serving Order</PageTitle>

      <Card className="mb-6">
        <ChoiceGroup question="Service type" hideLegend className="grid grid-cols-2 gap-4">
          <RadioCard
            name="serviceType"
            label="Court Case"
            description="Begin a Process Serving order for an already filed court case."
            checked={serviceType === 'court'}
            onChange={() => { setServiceType('court'); setStep(2); }}
          />
          <RadioCard
            name="serviceType"
            label="Other Notice"
            description="For formal serves without a court case (collections, notices to vacate, etc.)."
            checked={serviceType === 'other'}
            onChange={() => { setServiceType('other'); setStep(2); }}
          />
        </ChoiceGroup>
      </Card>

      {step >= 2 && serviceType === 'court' && (
        <Card className="mb-6">
          <SectionTitle>Court information</SectionTitle>
          <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>Enter the court information for the case in which you are serving. Fields marked with an asterisk (*) are required.</p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select
              label="Choose the subject matter jurisdiction"
              required
              options={FormData.jurisdictions}
              value={jurisdiction}
              onChange={handleJurisdictionChange}
            />
            <Select
              label="In which state would you like to serve"
              required
              options={FormData.states}
              value={selectedState}
              onChange={handleStateChange}
              disabled={jurisdiction === 'federal'}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select
              label="Choose the court"
              required
              options={courtOptions}
              value={selectedCourt}
              onChange={handleCourtChange}
            />
            <Select
              label="Choose the court location"
              required
              options={locationOptions}
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            />
          </div>
          <InfoBanner variant="warning">
            <span className="font-semibold">Not sure about the address or agent?</span> Before proceeding, consider using our <a href="#" style={{ color: brand.colors.link }} className="font-medium">Locate service</a> to ensure accuracy and increase the chances of successful service.
          </InfoBanner>
        </Card>
      )}

      {step >= 2 && serviceType === 'other' && (
        <Card className="mb-6">
          <SectionTitle>Notice Information</SectionTitle>
          <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>Provide details about the notice you need served.</p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select label="Notice Type" required options={FormData.noticeTypes} />
            <Select
              label="State"
              required
              options={FormData.states}
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            />
          </div>
          <Input label="Description" placeholder="Brief description of the notice..." className="mb-4" />
        </Card>
      )}

      {step >= 2 && (
        <Card className="mb-6">
          <SectionTitle>Party to Serve</SectionTitle>
          <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>Enter information about the party being served.</p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Input label="Name (as appears on court documents)" required />
            <Input label="Special Instructions" />
          </div>
          <ChoiceGroup question="Type:" className="mb-4" legendClassName="mb-2">
            <div className="flex gap-6">
              {FormData.partyTypes.map(type => (
                <label key={type.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="partyType"
                    value={type.value}
                    checked={partyType === type.value}
                    onChange={(e) => setPartyType(e.target.value)}
                    style={{ accentColor: brand.colors.primary }}
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </ChoiceGroup>

          <SectionTitle>Service Location</SectionTitle>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Select label="Address Type" options={FormData.addressTypes} />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Input label="Address Line 1" required placeholder="Street address" />
            <Input label="Address Line 2" placeholder="Apt, Suite, Unit, etc." />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Input label="City" required />
            <Select
              label="State"
              required
              options={FormData.states}
              value={addressState}
              onChange={(e) => setAddressState(e.target.value)}
            />
            <Input label="ZIP Code" required placeholder="00000" />
          </div>
        </Card>
      )}

      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={() => onNavigate('dashboard')}>CANCEL ORDER</Button>
        <Button disabled={step < 2} icon={Icons.ArrowRight}>CONTINUE</Button>
      </div>
    </div>
  );
};

export default CustomerServiceOfProcess;
